import { LOOK_SLOTS } from '@kopi-kaki/protocol';
import type { DrinkKind, Look } from '@kopi-kaki/protocol';

/**
 * Colour and style tables for avatar looks. A `Look` stores indices into
 * these arrays, so the arrays ARE the avatar options: reordering them changes
 * every existing player's appearance. Append only.
 *
 * The first eight entries of the hair-colour, top-colour and bottom-colour
 * tables match the original three-field looks, so legacy players convert
 * without changing appearance (see normaliseLook in the protocol).
 */

export const SKIN_TONES = [
  '#fbe0c6',
  '#f5cfa6',
  '#eab88a',
  '#d99c68',
  '#bd7f4d',
  '#9a6136',
  '#744524',
  '#f2c9b4',
] as const;

export const TOP_COLORS = [
  // Original shirts.
  '#d9433b',
  '#3a6fd6',
  '#3d9e4c',
  '#f0bf36',
  '#8a52c8',
  '#ef7d2c',
  '#2c9f9b',
  '#efeae0',
  // Added.
  '#2a2a33',
  '#f19bb7',
  '#7fb7d9',
  '#8fcf6a',
  '#b5563f',
  '#6b4226',
  '#c9c3b5',
  '#1f3a6b',
  '#e8604f',
  '#ffd966',
  '#5a2a5e',
  '#0f7a5a',
  '#f4a259',
  '#a3a8b0',
  '#ff7eb3',
  '#1d1d1d',
] as const;

export const TOP_COLOR_NAMES = [
  'Chilli', 'Sky', 'Pandan', 'Kaya', 'Ube', 'Papaya', 'Teal', 'Singlet',
  'Charcoal', 'Bandung', 'Ice Blue', 'Lime', 'Brick', 'Kopi', 'Stone', 'Navy',
  'Coral', 'Butter', 'Plum', 'Jade', 'Mango', 'Silver', 'Candy', 'Black',
];

export const BOTTOM_COLORS = [
  // Original trousers, one per original shirt.
  '#2e3a5c',
  '#3a3a44',
  '#5b4632',
  '#2c4a3a',
  '#4a2f50',
  '#34495e',
  '#5e2f2f',
  '#4a5a78',
  // Added.
  '#1d1d1d',
  '#c9c3b5',
  '#7a6450',
  '#3a6fd6',
  '#d9433b',
  '#efeae0',
  '#6b8f4a',
  '#b58a5c',
  '#8a52c8',
  '#f19bb7',
  '#2c9f9b',
  '#f0bf36',
  '#a3a8b0',
  '#5a3a2a',
  '#26303d',
  '#e8dcc4',
] as const;

export type HairStyle = 'short' | 'long' | 'spiky' | 'bun' | 'bob' | 'side' | 'curly' | 'buzz' | 'cap' | 'afro' | 'ponytail' | 'braids';

/**
 * Hair styles by `look.hair % 32`. Index 5 was the cap in the original looks.
 * Append only: reordering changes every existing player's hair.
 */
export const HAIR_STYLES: readonly { style: HairStyle; label: string }[] = [
  { style: 'short', label: 'Classic' },
  { style: 'long', label: 'Long' },
  { style: 'spiky', label: 'Spiky' },
  { style: 'bun', label: 'Bun' },
  { style: 'bob', label: 'Bob' },
  { style: 'side', label: 'Side part' },
  { style: 'curly', label: 'Curly' },
  { style: 'buzz', label: 'Uncle' },
  // Added.
  { style: 'afro', label: 'Afro' },
  { style: 'ponytail', label: 'Ponytail' },
  { style: 'braids', label: 'Braids' },
];

export const HAIR_COLORS = [
  // One per original hair option, in order.
  '#2a2320',
  '#6b3f22',
  '#e3b94f',
  '#1f1a18',
  '#b9532a',
  '#4a2c1a',
  '#e27fae',
  '#cfc9c1',
  // Added.
  '#8a8a8a',
  '#f4e3b0',
  '#3a6fd6',
  '#3d9e4c',
  '#8a52c8',
  '#d9433b',
  '#f07f2a',
  '#7a4a2e',
] as const;

export const CAP_COLOR = '#c8423b';

export interface HairOption {
  style: HairStyle;
  color: string;
  /** Only used by the cap style. */
  accent?: string;
}

export const SHOE_COLOR = '#2b2320';
export const EYE_COLOR = '#1d1716';
export const OUTLINE_COLOR = '#2a1e19';

/**
 * Shoe colours, picked independently of the shoe style. Index 0 matches the
 * original fixed shoe colour, so existing looks (which default to 0) are
 * unchanged. Append only.
 */
export const SHOE_COLORS = [
  '#2b2320',
  '#fffaf0',
  '#d9433b',
  '#3a6fd6',
  '#3d9e4c',
  '#f0bf36',
  '#8a52c8',
  '#efeae0',
  '#7fb7d9',
  '#f19bb7',
  '#8fcf6a',
  '#6b4226',
  '#1f3a6b',
  '#a3a8b0',
  '#ff7eb3',
  '#1d1d1d',
] as const;

export const DRINK_COLORS: Readonly<Record<DrinkKind, string>> = {
  kopi: '#a8744c',
  kopi_o: '#3a2214',
  kopi_c: '#bd8c62',
  teh_tarik: '#d8aa7a',
  milo: '#6b4428',
  bandung: '#f19bb7',
};

const at = <T,>(list: readonly T[], index: number): T => list[((index % list.length) + list.length) % list.length]!;

export function skinOf(index: number): string {
  return at(SKIN_TONES, index);
}

export function topColorOf(index: number): string {
  return at(TOP_COLORS, index);
}

export function bottomColorOf(index: number): string {
  return at(BOTTOM_COLORS, index);
}

export function hairColorOf(index: number): string {
  return at(HAIR_COLORS, index);
}

export function shoesColorOf(index: number): string {
  return at(SHOE_COLORS, index);
}

/** The hair to draw for a look. A cap (hat 1) replaces the hairstyle. */
export function hairOf(look: Look): HairOption {
  const color = hairColorOf(look.hairColor);
  if (look.hat === 1) return { style: 'cap', color, accent: CAP_COLOR };
  return { style: at(HAIR_STYLES, look.hair).style, color };
}

/* -------------------------------------------------------------------------- */
/* Clothing and accessory styles                                              */
/* -------------------------------------------------------------------------- */

/**
 * Style tables for the character designer. A look's index picks an entry
 * modulo the table length, so these are append-only like the colours.
 */
export const EYE_STYLES = ['Dots', 'Happy', 'Sleepy', 'Lashes', 'Determined', 'Sparkle', 'Wink', 'Starlight'] as const;
export const TOP_STYLES = [
  'T-shirt', 'Singlet', 'Polo', 'Office Shirt', 'Hoodie', 'Kebaya', 'Jersey', 'Apron',
  // Added.
  'Batik Shirt', 'Varsity Jacket', 'Turtleneck',
] as const;
export const BOTTOM_STYLES = ['Trousers', 'Shorts', 'Skirt', 'Sarong', 'Cargo Pants', 'Overalls'] as const;
export const SHOE_STYLES = ['Sneakers', 'Slippers', 'Boots', 'Sandals', 'Loafers', 'Heels'] as const;
export const HAT_STYLES = [
  'None', 'Red Cap', 'Beanie', 'Straw Hat', 'Chef Hat', 'Hard Hat', 'Headphones', 'Flower Clip', 'Party Hat', 'Top Hat', 'Golden Crown',
  // Added.
  'Beret', 'Bandana', 'Firecracker Crown',
] as const;
export const GLASSES_STYLES = ['None', 'Round', 'Shades', 'Nerd', 'Hearts', 'Cat-eye', 'Monocle', 'Diamond Shades'] as const;
export const PROP_STYLES = ['None', 'Red Lightsaber', 'Blue Lightsaber', 'Green Lightsaber', 'Purple Lightsaber', 'Pink Lightsaber', 'White Lightsaber'] as const;

export function styleIndex(list: readonly unknown[], index: number): number {
  return ((index % list.length) + list.length) % list.length;
}

/* -------------------------------------------------------------------------- */
/* Player designs                                                             */
/* -------------------------------------------------------------------------- */

/**
 * Designs (look.print) are fetched from the server on demand; the renderer
 * asks this resolver for the pixel string synchronously and draws a plain top
 * until it arrives. The wardrobe feature installs the real resolver.
 */
type DesignResolver = (designId: string) => string | undefined;
let designResolver: DesignResolver = () => undefined;

export function setDesignResolver(resolver: DesignResolver): void {
  designResolver = resolver;
}

export function designPixels(designId: string | null): string | undefined {
  return designId ? designResolver(designId) : undefined;
}

/**
 * Stable cache key covering every field of a look. A print only counts once
 * its pixels have loaded, so the sprite is redrawn when they arrive.
 */
export function lookKey(look: Look): string {
  const print = look.print ? `${look.print}${designPixels(look.print) ? '+' : ''}` : '-';
  return `${LOOK_SLOTS.map((slot) => look[slot]).join('.')}.${print}`;
}
